"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import {
  emptyFilterGroup,
  type RecordFilterGroup,
} from "@shared/records/filter-clause";
import {
  safeLocalStorage,
  safeSessionStorage,
} from "@/lib/storage/safe-storage";
import {
  parseFilterGroup,
  parseSort,
  serializeFilterGroup,
  serializeSort,
} from "../filters/filter-serialize";
import { recordUrlStateStorageKey } from "./record-url-state-storage";
import type {
  RecordFieldDef,
  RecordLegacyUrlAdapter,
  RecordPresentationMode,
  RecordSort,
} from "../types";

export interface RecordUrlState {
  filter: RecordFilterGroup;
  search: string;
  sort: RecordSort | undefined;
  page: number;
  pageSize: number;
  viewId: string | undefined;
  columnOrder: string[] | undefined;
  columnHidden: string[] | undefined;
  presentationMode: RecordPresentationMode;
}

interface UseRecordUrlStateOptions {
  objectId: string;
  fields: Map<string, RecordFieldDef>;
  fieldAliases?: Record<string, string>;
  defaultSort: RecordSort | undefined;
  defaultPageSize: number;
  presentationModes: RecordPresentationMode[];
  defaultPresentationMode: RecordPresentationMode;
  namespace?: string;
  legacyUrl?: RecordLegacyUrlAdapter;
  syncUrl?: boolean;
}

function paramKey(name: string, namespace: string | undefined) {
  return namespace ? `${namespace}-${name}` : name;
}

function parsePresentationMode(
  raw: string | null,
  supported: RecordPresentationMode[],
  defaultMode: RecordPresentationMode,
): RecordPresentationMode {
  if (raw === "table" && supported.includes(raw)) return raw;
  if (raw === "board" && supported.includes(raw)) return raw;
  if (raw === "calendar" && supported.includes(raw)) return raw;
  return supported.includes(defaultMode) ? defaultMode : "table";
}

const MIN_PAGE = 1;
const MAX_RECORD_OFFSET = 10_000;
const ALLOWED_PAGE_SIZES = [25, 50, 100] as const;

function pageSizeStorageKey(objectId: string): string {
  return `record-page-size:${objectId}`;
}

function boundedInteger(value: number, minimum: number, maximum: number) {
  if (!Number.isFinite(value)) return minimum;
  return Math.min(maximum, Math.max(minimum, Math.floor(value)));
}

function boundedPageSize(value: number, fallback: number) {
  return ALLOWED_PAGE_SIZES.some((size) => size === value) ? value : fallback;
}

function readStoredPageSize(objectId: string, fallback: number): number {
  const stored = Number(
    safeLocalStorage().getItem(pageSizeStorageKey(objectId)),
  );
  return boundedPageSize(stored, fallback);
}

function storePageSize(objectId: string, pageSize: number): void {
  safeLocalStorage().setItem(pageSizeStorageKey(objectId), String(pageSize));
}

function parseList(raw: string | null): string[] | undefined {
  if (!raw) return undefined;
  return raw.split(",").filter(Boolean);
}

function parseUrlFilter(
  params: URLSearchParams,
  rawFilter: string | null,
  {
    fields,
    fieldAliases,
    legacyUrl,
  }: Pick<UseRecordUrlStateOptions, "fields" | "fieldAliases" | "legacyUrl">,
): RecordFilterGroup {
  const canonicalFilter = parseFilterGroup(rawFilter, fields, fieldAliases);
  const canonicalFields = new Set(
    canonicalFilter.conditions.map((condition) => condition.field),
  );
  const legacyConditions = (legacyUrl?.conditions(params) ?? []).filter(
    (condition) => !canonicalFields.has(condition.field),
  );
  return {
    conjunction: "and",
    conditions: [...canonicalFilter.conditions, ...legacyConditions],
  };
}

function parseFrom(
  params: URLSearchParams,
  {
    objectId,
    fields,
    fieldAliases,
    defaultSort,
    defaultPageSize,
    presentationModes,
    defaultPresentationMode,
    namespace,
    legacyUrl,
  }: UseRecordUrlStateOptions,
): RecordUrlState {
  const key = (name: string) => paramKey(name, namespace);
  const page = Number(params.get(key("page")) ?? "1");
  const pageSize = Number(
    params.get(key("per")) ??
      String(readStoredPageSize(objectId, defaultPageSize)),
  );
  const boundedSize = boundedPageSize(pageSize, defaultPageSize);
  const filter = parseUrlFilter(
    params,
    params.get(key("f")),
    { fields, fieldAliases, legacyUrl },
  );
  return {
    filter,
    search: params.get(key("q")) ?? "",
    sort: parseSort(params.get(key("sort")), fields) ?? defaultSort,
    page: boundedInteger(
      page,
      MIN_PAGE,
      Math.floor(MAX_RECORD_OFFSET / boundedSize) + 1,
    ),
    pageSize: boundedSize,
    viewId: params.get(key("view")) ?? undefined,
    columnOrder: parseList(params.get(key("cols"))),
    columnHidden: parseList(params.get(key("hide"))),
    presentationMode: parsePresentationMode(
      params.get(key("layout")),
      presentationModes,
      defaultPresentationMode,
    ),
  };
}

export const TABLE_PARAM_KEYS = [
  "f",
  "q",
  "sort",
  "page",
  "per",
  "view",
  "cols",
  "hide",
  "layout",
];

const PER_VISIT_PARAM_KEYS = ["q", "page"];

function hasTableParams(
  params: URLSearchParams,
  options: UseRecordUrlStateOptions,
): boolean {
  const hasCanonicalParam = TABLE_PARAM_KEYS.some((name) =>
    params.has(paramKey(name, options.namespace)),
  );
  return (
    hasCanonicalParam ||
    (options.legacyUrl?.paramKeys.some((name) => params.has(name)) ?? false)
  );
}

function buildQuery(
  state: RecordUrlState,
  defaultSort: RecordSort | undefined,
  defaultPageSize: number,
  defaultPresentationMode: RecordPresentationMode,
  current: string,
  namespace?: string,
  legacyParamKeys: string[] = [],
): string {
  const params = new URLSearchParams(current);
  const key = (name: string) => paramKey(name, namespace);
  for (const name of TABLE_PARAM_KEYS) params.delete(key(name));
  for (const legacyParamKey of legacyParamKeys) params.delete(legacyParamKey);
  const filterString = serializeFilterGroup(state.filter);
  if (filterString) params.set(key("f"), filterString);
  if (state.search) params.set(key("q"), state.search);

  const sortString = serializeSort(state.sort);
  if (sortString && sortString !== serializeSort(defaultSort)) {
    params.set(key("sort"), sortString);
  }
  if (state.page > 1) params.set(key("page"), String(state.page));
  if (state.pageSize !== defaultPageSize) {
    params.set(key("per"), String(state.pageSize));
  }
  if (state.viewId) params.set(key("view"), state.viewId);
  if (state.columnOrder?.length)
    params.set(key("cols"), state.columnOrder.join(","));
  if (state.columnHidden?.length)
    params.set(key("hide"), state.columnHidden.join(","));
  if (state.presentationMode !== defaultPresentationMode) {
    params.set(key("layout"), state.presentationMode);
  }
  return params.toString();
}

function storeUrlState(
  state: RecordUrlState,
  options: UseRecordUrlStateOptions,
  pathname: string,
): void {
  const query = buildQuery(
    state,
    options.defaultSort,
    options.defaultPageSize,
    options.defaultPresentationMode,
    "",
    options.namespace,
  );
  safeSessionStorage().setItem(
    recordUrlStateStorageKey(pathname, options.objectId, options.namespace),
    query,
  );
}

function restoreUrlState(
  current: URLSearchParams,
  options: UseRecordUrlStateOptions,
  pathname: string,
): { params: URLSearchParams; restored: boolean } {
  if (hasTableParams(current, options)) {
    return { params: current, restored: false };
  }
  const stored = safeSessionStorage().getItem(
    recordUrlStateStorageKey(pathname, options.objectId, options.namespace),
  );
  if (stored === null) return { params: current, restored: false };
  const params = new URLSearchParams(current);
  const perVisitKeys = new Set(
    PER_VISIT_PARAM_KEYS.map((name) => paramKey(name, options.namespace)),
  );
  new URLSearchParams(stored).forEach((value, key) => {
    if (!perVisitKeys.has(key)) params.set(key, value);
  });
  return { params, restored: params.toString() !== current.toString() };
}

function urlFor(pathname: string, query: string): string {
  return `${query ? `${pathname}?${query}` : pathname}${window.location.hash}`;
}

function adoptUrlState(
  options: UseRecordUrlStateOptions,
  pathname: string,
): RecordUrlState {
  const restored = restoreUrlState(
    new URLSearchParams(window.location.search),
    options,
    pathname,
  );
  const nextState = parseFrom(restored.params, options);
  storeUrlState(nextState, options, pathname);
  if (restored.restored) {
    window.history.replaceState(null, "", urlFor(pathname, restored.params.toString()));
  }
  return nextState;
}

export function useRecordUrlState(options: UseRecordUrlStateOptions) {
  const pathname = usePathname();
  const syncUrl = options.syncUrl ?? true;
  const latestOptions = useRef(options);
  useEffect(() => {
    latestOptions.current = options;
  }, [options]);
  const [state, setState] = useState<RecordUrlState>(() =>
    parseFrom(
      new URLSearchParams(
        typeof window === "undefined" || !syncUrl ? "" : window.location.search,
      ),
      options,
    ),
  );
  const [isReady, setIsReady] = useState(false);
  const stateRef = useRef(state);
  const applyState = useCallback((next: RecordUrlState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  useEffect(() => {
    const adopt = () =>
      applyState(adoptUrlState(latestOptions.current, pathname));
    const hydrationTimer = window.setTimeout(() => {
      if (syncUrl) adopt();
      setIsReady(true);
    }, 0);
    if (!syncUrl) return () => window.clearTimeout(hydrationTimer);
    const onPopState = adopt;
    window.addEventListener("popstate", onPopState);
    return () => {
      window.clearTimeout(hydrationTimer);
      window.removeEventListener("popstate", onPopState);
    };
  }, [applyState, pathname, syncUrl]);

  const write = useCallback(
    (next: Partial<RecordUrlState>) => {
      if (next.pageSize !== undefined) {
        storePageSize(
          options.objectId,
          boundedPageSize(next.pageSize, options.defaultPageSize),
        );
      }
      const current = stateRef.current;
      const merged = {
        ...current,
        ...next,
        pageSize: boundedPageSize(
          next.pageSize ?? current.pageSize,
          options.defaultPageSize,
        ),
      };
      merged.page = boundedInteger(
        next.page ?? current.page,
        MIN_PAGE,
        Math.floor(MAX_RECORD_OFFSET / merged.pageSize) + 1,
      );
      applyState(merged);
      if (syncUrl) {
        const query = buildQuery(
          merged,
          options.defaultSort,
          options.defaultPageSize,
          options.defaultPresentationMode,
          window.location.search,
          options.namespace,
          options.legacyUrl?.paramKeys,
        );
        window.history.replaceState(null, "", urlFor(pathname, query));
        storeUrlState(merged, latestOptions.current, pathname);
      }
    },
    [
      applyState,
      pathname,
      options.objectId,
      options.defaultSort,
      options.defaultPageSize,
      options.defaultPresentationMode,
      options.namespace,
      options.legacyUrl,
      syncUrl,
    ],
  );

  const reset = useCallback(() => {
    write({
      filter: emptyFilterGroup(),
      search: "",
      page: 1,
      viewId: undefined,
    });
  }, [write]);

  return useMemo(
    () => ({ state, write, reset, isReady }),
    [state, write, reset, isReady],
  );
}
